import React, { createContext, useState, useContext } from 'react';

/**
 * Provider that keeps context of the error banner's open || closed state.
 * This provider also exposes control methods to close the error via 'useErrorBanner' hook. Example:
 * ```jsx
 * import { useErrorBanner } from '@src/context/ErrorBanner';
 *
 * const { closeErrorBanner, openErrorBanner } = useErrorBanner();
 *
 * <Button onClick={openErrorBanner}>Open Error</Button>
 * <Button onClick={closeErrorBanner}>Close Error</Button>
 * ```
 *
 * @packageDocumentation
 */
type ErrorBannerProps = {
  showErrorBanner: boolean;
  openErrorBanner: () => void;
  closeErrorBanner: () => void;
};

const ErrorBanner = createContext<ErrorBannerProps | undefined>(undefined);

const ErrorBannerProvider: React.FC = ({ children }) => {
  const [showErrorBanner, setShowErrorBanner] = useState(false);

  const openErrorBanner = () => {
    setShowErrorBanner(true);
  };

  const closeErrorBanner = () => {
    setShowErrorBanner(false);
  };

  return (
    <ErrorBanner.Provider
      value={{
        showErrorBanner,
        closeErrorBanner,
        openErrorBanner,
      }}
    >
      {children}
    </ErrorBanner.Provider>
  );
};

const useErrorBanner = () => {
  const context = useContext(ErrorBanner);

  /* istanbul ignore if */
  if (context === undefined) {
    throw new Error('useErrorBanner must be used within a ErrorBannerProvider.');
  }

  return context;
};

export default ErrorBannerProvider;

export { useErrorBanner };
